import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  FlatActionList,
  Page,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { QUERY_LLM_STRATEGIES } from "../../common";
import { useLazyQuery } from "@apollo/client";
import { AiStrategy } from "@towersystems/roam-common/lib/generated-types";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useFocusEffect } from "@react-navigation/native";
import React from "react";
import {
  useGlobalSettingsProvider,
  useMutationUpdateGlobalSettings,
} from "../../../../utilities/global-settings/hooks";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Colors } from "react-native-ui-lib";
import { Feather } from "@expo/vector-icons";

export interface SelectImageQuickWinStrategyScreenProps
  extends StackScreenProps<any, "SelectImageQuickWinStrategyScreen"> {}

export const SelectImageQuickWinStrategyScreen = ({
  navigation,
}: SelectImageQuickWinStrategyScreenProps) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { aiImageStrategyId } = useGlobalSettingsProvider();
  const { updateGlobalSettings } = useMutationUpdateGlobalSettings();
  const flashMessagesContext = useFlashMessages();

  const [selectedAiStrategyId, setSelectedAiStrategyId] = React.useState<
    string | null
  >(aiImageStrategyId || null);

  const [aiStrategies, setLlmStrategies] = React.useState<
    AiStrategy[] | undefined
  >(undefined);

  const { t } = useTranslation("settings");

  const [queryLlmStrategies] = useLazyQuery<{
    aiStrategies: {
      items: AiStrategy[];
    };
  }>(QUERY_LLM_STRATEGIES);

  const transitionEnded = useHasTransitionEnded(navigation);

  useFocusEffect(
    React.useCallback(() => {
      (async () => {
        try {
          setSelectedAiStrategyId(aiImageStrategyId || null);
          const [aiStrategiesResult] = await Promise.all([
            queryLlmStrategies(),
          ]);
          setLlmStrategies(aiStrategiesResult.data?.aiStrategies.items);
        } catch (error) {}
      })();
    }, [aiImageStrategyId])
  );

  if (aiStrategies === undefined || !transitionEnded) {
    return <FlatListSearchableSkeleton searchbar={false} />;
  }

  const handleOnSelect = (strategyId: string) => {
    if (selectedAiStrategyId === strategyId) {
      setSelectedAiStrategyId(null);
      return;
    }
    setSelectedAiStrategyId(strategyId);
  };

  const handleOnSubmit = async (strategyId: string | null) => {
    try {
      setIsSubmitting(true);
      await updateGlobalSettings({
        aiImageStrategyId: strategyId as any,
      });
      if (strategyId === null) {
        flashMessagesContext.showMessage({
          message: "AI Strategy has been removed",
          severity: "success",
        });
      } else {
        flashMessagesContext.showMessage({
          message: "AI Strategy has been updated",
          severity: "success",
        });
      }
      navigation.goBack();
    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const isDirty = selectedAiStrategyId != aiImageStrategyId;

  const footerMarkup = (
    <Button
      disabled={!isDirty || isSubmitting}
      label={t("button.select_llm_strategy")}
      backgroundColor={Colors.action}
      color="white"
      onPress={() => handleOnSubmit(selectedAiStrategyId)}
    ></Button>
  );

  const items =
    aiStrategies
      ?.filter((aiStrategy) => aiStrategy.aiType === "image-generation")
      ?.map((aiStrategy) => ({
        key: aiStrategy.id,
        title: aiStrategy.name || "Unnamed strategy",
        subTitle: aiStrategy.config.code || "",
        selected: selectedAiStrategyId === aiStrategy.id,
        onPress: () => {
          handleOnSelect(aiStrategy.id);
        },
        componentRight: () => {
          return aiStrategy.id === selectedAiStrategyId ? (
            <Feather name="check" size={24} color={Colors.action} />
          ) : null;
        },
      })) || [];

  return (
    <Page
      title={t("title.select_image_quick_win_strategy")}
      secondaryTitle={t("title.select_image_quick_win_strategy_secondary")}
      footer={footerMarkup}
    >
      <View>
        <FlatActionList items={items} />
      </View>
    </Page>
  );
};
